import { useCallback, useContext, useEffect, useId, useRef } from "react";
import { SpeechToTextContext } from "./context";

// Per-field hook for TaskTextarea: identifies this field to the shared SpeechToTextProvider
// and derives this field's own recording/connecting state from the single shared connection.
export function useSpeechToTextRecorder(onCommittedText: (text: string) => void) {
    const context = useContext(SpeechToTextContext);
    const fieldId = useId();

    const onCommittedTextRef = useRef(onCommittedText);
    onCommittedTextRef.current = onCommittedText;

    // Kept up to date every render so the unmount cleanup below can call the latest
    // context.stop without re-subscribing the effect (fieldId never changes).
    const contextRef = useRef(context);
    contextRef.current = context;

    useEffect(() => {
        return () => {
            // No-ops if this field wasn't the one holding the mic — releases it if it was,
            // so navigating away (or the task closing) mid-recording doesn't leave the
            // connection running with nothing listening for its transcripts.
            contextRef.current.stop(fieldId);
        };
    }, [fieldId]);

    const isActive = context.activeFieldId === fieldId;
    const isRecording = isActive && context.status === "recording";
    const isConnecting = isActive && context.status === "connecting";
    // context.partialText is shared/global (whichever field is active) — only relevant here.
    const partialText = isActive ? context.partialText : "";
    const sessionLimitReached = context.sessionLimitFieldId === fieldId;

    const toggle = useCallback(() => {
        if (isActive) {
            context.stop(fieldId);
            return;
        }
        // Permission is already known to be denied — let the popover/title explain why
        // instead of attempting (and silently failing) to connect.
        if (context.micPermissionDenied) return;
        context.start(fieldId, (text) => onCommittedTextRef.current(text));
    }, [context, fieldId, isActive]);

    return {
        isRecording,
        isConnecting,
        micPermissionDenied: context.micPermissionDenied,
        partialText,
        sessionLimitReached,
        toggle,
    };
}
